import type {
    AlgorithmPatternRawData,
    TrendPatternLine,
    TrendPatternRawData,
} from '../../../../types/patternRecognition';

type StructuredPatternRawData = TrendPatternRawData | AlgorithmPatternRawData;

export interface FibonacciLineLabel {
    time: number;
    text: string;
    touched: boolean;
}

export interface TrendLineLabel {
    time: number;
    price: number;
    text: string;
}

const TREND_LINE_LABELS: Record<string, string> = {
    neckline: '颈线',
    breakout: '突破线',
    upper: '上轨',
    lower: '下轨',
    flag_upper: '旗形上轨',
    flag_lower: '旗形下轨',
    pennant_upper: '三角旗上轨',
    pennant_lower: '三角旗下轨',
    pole: '旗杆',
    left_upper: '左上边界',
    right_upper: '右上边界',
    left_lower: '左下边界',
    right_lower: '右下边界',
    left_arm: '左侧形态线',
    right_arm: '右侧形态线',
    head_left: '头部左侧',
    head_right: '头部右侧',
    cup_left: '杯体左侧',
    cup_right: '杯体右侧',
    structure: '形态轮廓',
};

/**
 * 返回趋势结构线的中文类型标注及其在线上的定位点。
 */
export function resolveTrendLineLabel(
    patternData: StructuredPatternRawData,
    line: TrendPatternLine,
    points: TrendPatternLine['points'] = line.points,
): TrendLineLabel | null {
    if ('algorithm' in patternData || points.length < 2) return null;
    const text = TREND_LINE_LABELS[line.key]
        ?? (line.type === 'horizontal' ? '水平线'
            : line.type === 'ray' ? '延伸线'
                : line.type === 'curve' ? '曲线'
                    : line.type === 'polyline' ? '形态轮廓' : '结构线');
    const middleIndex = Math.floor(points.length / 2);
    if (points.length > 2) {
        return {...points[middleIndex], text};
    }
    const [start, end] = points;
    return {
        time: start.time + Math.floor((end.time - start.time) / 2),
        price: start.price + (end.price - start.price) / 2,
        text,
    };
}

/**
 * 为旧版三重顶结果补齐峰谷轮廓线。
 *
 * 历史 rawData 只保存了颈线，但五个锚点是完整的；按时间排序后恢复
 * PEAK-TROUGH-PEAK-TROUGH-PEAK，避免旧信号必须重新入库后才能正确展示。
 */
export function resolvePatternOverlayLines(
    patternData: StructuredPatternRawData,
): TrendPatternLine[] {
    const lines = patternData.lines ?? [];
    if ('algorithm' in patternData || lines.some(line => line.key === 'structure')) {
        return lines;
    }

    const tripleTopLabels = new Set(['PEAK1', 'TROUGH1', 'PEAK2', 'TROUGH2', 'PEAK3']);
    const structureAnchors = (patternData.anchors ?? [])
        .filter(anchor => tripleTopLabels.has(anchor.label?.toUpperCase()))
        .sort((a, b) => a.time - b.time);
    if (structureAnchors.length !== 5
        || structureAnchors.map(anchor => anchor.label?.toUpperCase()).join(',')
        !== 'PEAK1,TROUGH1,PEAK2,TROUGH2,PEAK3') {
        return lines;
    }

    return [{
        key: 'structure',
        type: 'polyline',
        points: structureAnchors.map(anchor => ({time: anchor.time, price: anchor.price})),
    }, ...lines];
}

/**
 * 统一结构线的显示边界。
 *
 * 旧版 Fibonacci rawData 将水平线保存为“波段终点 → 确认K线”，短于信号区间。
 * 左边缘以持久化的 windowStartTime 为准，右边缘可延伸至图表最新真实K线。
 */
export function resolvePatternLinePoints(
    patternData: StructuredPatternRawData,
    line: TrendPatternLine,
    latestCandleTime?: number,
): TrendPatternLine['points'] {
    if (!('algorithm' in patternData) && line.key === 'breakout') {
        const rightShoulder = patternData.anchors?.find(
            anchor => anchor.label?.toUpperCase() === 'RIGHT_SHOULDER',
        );
        if (rightShoulder && line.points?.length >= 2) {
            return [
                {time: rightShoulder.time, price: rightShoulder.price},
                ...line.points.slice(1),
            ];
        }
    }
    if (!('algorithm' in patternData) && line.key === 'neckline' && line.type === 'horizontal') {
        const troughs = (patternData.anchors ?? [])
            .filter(anchor => /^TROUGH[12]$/i.test(anchor.label))
            .sort((a, b) => a.time - b.time);
        const hasTripleTopPeaks = (patternData.anchors ?? [])
            .filter(anchor => /^PEAK[123]$/i.test(anchor.label)).length === 3;
        if (hasTripleTopPeaks && troughs.length === 2 && troughs[1].time > troughs[0].time) {
            const endTime = patternData.confirmCandleTime;
            const slope = (troughs[1].price - troughs[0].price) / (troughs[1].time - troughs[0].time);
            return [
                {time: troughs[0].time, price: troughs[0].price},
                {time: endTime, price: troughs[0].price + slope * (endTime - troughs[0].time)},
            ];
        }
    }

    const isFibonacciLevel = 'algorithm' in patternData
        && patternData.scope === 'ALGORITHM'
        && patternData.algorithm === 'FIBONACCI_RETRACEMENT'
        && line.key.startsWith('fib_');
    const startTime = patternData.windowStartTime;
    const signalEndTime = patternData.windowEndTime;
    const firstPoint = line.points?.[0];

    if (!isFibonacciLevel
        || null == firstPoint
        || null == startTime
        || null == signalEndTime
        || !Number.isFinite(startTime)
        || !Number.isFinite(signalEndTime)
        || signalEndTime <= startTime) {
        return line.points;
    }
    const normalizedLatestTime = normalizeTimeUnit(latestCandleTime, signalEndTime);
    const displayEndTime = null != normalizedLatestTime && normalizedLatestTime > signalEndTime
        ? normalizedLatestTime : signalEndTime;

    // Fibonacci 级别保持严格水平，并向右覆盖到当前图表最后一根真实K线。
    return [
        {time: startTime, price: firstPoint.price},
        {time: displayEndTime, price: firstPoint.price},
    ];
}

/**
 * 返回 Fibonacci 水平线中部标签，例如 “61.8% · 98,250.50”。
 */
export function resolveFibonacciLineLabel(
    patternData: StructuredPatternRawData,
    line: TrendPatternLine,
    latestCandleTime?: number,
): FibonacciLineLabel | null {
    if (!('algorithm' in patternData)
        || patternData.algorithm !== 'FIBONACCI_RETRACEMENT'
        || !line.key.startsWith('fib_')) {
        return null;
    }
    const points = resolvePatternLinePoints(patternData, line, latestCandleTime);
    if (points.length < 2) return null;

    const ratio = Number(line.key.slice(4).replace(/_/g, '.'));
    const price = Number(points[0].price);
    const startTime = Number(points[0].time);
    const endTime = Number(points[points.length - 1].time);
    if (![ratio, price, startTime, endTime].every(Number.isFinite) || endTime <= startTime) {
        return null;
    }

    const touchedRatio = resolveTouchedFibonacciRatio(patternData) ?? 0;
    return {
        time: startTime + Math.floor((endTime - startTime) / 2),
        text: `${formatFibonacciRatio(ratio)} · ${formatOverlayPrice(price)}`,
        touched: Number.isFinite(touchedRatio) && Math.abs(touchedRatio - ratio) < 0.000_001,
    };
}

function normalizeTimeUnit(candidate: number | undefined, reference: number): number | null {
    if (null == candidate || !Number.isFinite(candidate)) return null;
    if (candidate <= 10_000_000_000 && reference > 10_000_000_000) {
        return candidate * 1000;
    }
    if (candidate > 10_000_000_000 && reference <= 10_000_000_000) {
        return Math.floor(candidate / 1000);
    }
    return candidate;
}

/**
 * 解析斐波那契波段的区间时间 [起点, 波段终点]。
 * rawData.anchors 中两个 Swing 端点（Swing Low / Swing High）即区间两端；
 * 数据缺失时返回 null，由调用方回退 klineStartTime/klineEndTime。
 */
export function resolveFibonacciSwingRange(
    patternData: AlgorithmPatternRawData | null | undefined,
): { startTime: number; endTime: number } | null {
    const swingTimes = (patternData?.anchors ?? [])
        .filter(anchor => /^swing\s+(low|high)$/i.test(anchor.label ?? ''))
        .map(anchor => Number(anchor.time))
        .filter(time => Number.isFinite(time))
        .sort((a, b) => a - b);
    if (swingTimes.length < 2) return null;
    const startTime = swingTimes[0];
    const endTime = swingTimes[swingTimes.length - 1];
    return endTime > startTime ? {startTime, endTime} : null;
}

/**
 * 以实际触及价重新裁决命中比例，修正旧数据中因容差带重叠而误存的 touchedRatio。
 */
export function resolveTouchedFibonacciRatio(
    patternData: AlgorithmPatternRawData | null | undefined,
): number | null {
    const levels = patternData?.keyLevels;
    const touchPrice = Number(levels?.touchPrice);
    const candidates = [
        {ratio: 0.382, price: Number(levels?.fib382)},
        {ratio: 0.5, price: Number(levels?.fib500)},
        {ratio: 0.618, price: Number(levels?.fib618)},
    ].filter(candidate => Number.isFinite(candidate.price));

    if (Number.isFinite(touchPrice) && candidates.length > 0) {
        return candidates.reduce((best, current) => {
            const bestDistance = Math.abs(touchPrice - best.price);
            const currentDistance = Math.abs(touchPrice - current.price);
            return currentDistance < bestDistance
            || (currentDistance === bestDistance && current.ratio > best.ratio)
                ? current : best;
        }).ratio;
    }

    const persistedRatio = Number(levels?.touchedRatio);
    return Number.isFinite(persistedRatio) ? persistedRatio : null;
}

function formatFibonacciRatio(ratio: number): string {
    return `${(ratio * 100).toFixed(1).replace(/\.0$/, '')}%`;
}

/**
 * 根据价格数量级保留有效精度，兼容高价币与 PEPE 一类小数价格。
 */
export function formatOverlayPrice(price: number): string {
    const absolute = Math.abs(price);
    const maximumFractionDigits = absolute >= 1_000 ? 2
        : absolute >= 1 ? 4
            : absolute >= 0.01 ? 6 : 8;
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits,
        useGrouping: true,
    }).format(price);
}
